import {
  ajax
} from 'application/src/js/functions.js';

/**
 * @description Modal Window for loading/displaying contact data of a specific user
 * @param {ModalWindow} args.modal ModalWindow instance
 * @param {object} args.translations text translations
 * @param {Mediator} args.mediator Mediator instance
 */
export default class ContactDetailsModal {
  constructor(args) {
    this.modal = args.modal;
    this.translations = args.translations;
    this.mediator = args.mediator;

    this.contentArea = this.modal.element.querySelector('.contact-details');
    this.loader = this.modal.element.querySelector('#load-contact-details-overlay');

    this.contactId = 0;
    this.data = {};

    this.addGeneralEvents();
  }

  addGeneralEvents() {
    this.mediator.subscribe('showNotes', _ => this.modal.element.classList.add('hide'));
    this.mediator.subscribe('hideNotes', _ => this.modal.element.classList.remove('hide'));
  }

  addEvents() {
    if (this.showNotesButton) {
      this.showNotesButton.addEventListener('click', e => {
        import( /* webpackChunkName: "manage-user-notes" */ 'application/src/js/user-notes/manage-user-notes.js')
          .then(({ default: ManageUserNotesController }) => {
            const userName = this.data.global_address_contact_id ?
            (this.data.firstname ? this.data.firstname + ' ' : '') + (this.data.lastname ? this.data.lastname : '') :
            (this.data.user_firstname ? this.data.user_firstname + ' ' : '') + (this.data.user_lastname ? this.data.user_lastname : '');

            const manageUserNotesController = new ManageUserNotesController({
              isModal: true,
              subHeadline: this.data.address_name,
              searchString: userName
            });
            manageUserNotesController.modal.subscribe('open', _ => this.mediator.publish('showNotes'));
            manageUserNotesController.modal.subscribe('close', _ => this.mediator.publish('hideNotes'));
            manageUserNotesController.load(this.addressId);
          });
      });
    }
  }

  /**
   * loads the contact/address info for a specific user, then starts rendering
   * @param {number} contactId
   * @param {number} userId
   * @param {number} addressId
   */
  show(contactId, userId, addressId) {
    this.contentArea.innerHTML = '';
    this.contactId = contactId;
    this.addressId = addressId;
    this.loader.style.display = 'block';
    this.modal.open();

    ajax('/analytics/clickDetails/ajaxLoadContactInfo', {
        global_address_contact_id: this.contactId,
        global_address_id: this.addressId,
        company_user_id: userId
      })
      .then(response => {
        this.data = Object.assign({}, response.data.address, response.data.contact);
        this.render();
        this.loader.style.display = 'none';
      })
      .catch(e => console.log(e));
  }

  /**
   * inserts the template for the contact details
   */
  render() {
    this.contentArea.innerHTML = this.template;
    this.showNotesButton = this.contentArea.querySelector('.show-notes');

    this.addEvents();
  }

  /**
   * @returns {string} `${lastname}, ${firstname}`
   */
  getUserFullName() {
    let userFullName = '';

    if (this.data.global_address_contact_id) {
      userFullName += this.data.lastname ? this.data.lastname + `, ` : '';
      userFullName += this.data.firstname ? this.data.firstname : '';
    } else {
      userFullName += this.data.user_lastname ? this.data.user_lastname + `, ` : '';
      userFullName += this.data.user_firstname ? this.data.user_firstname : '';
    }

    return this.userFullName = userFullName.trim();
  }

  get template() {
    return `
      <div class="image">
        <div class="user-image large" title="${this.getUserFullName()}">
          <i class="fas fa fa-user"></i>
          <div style="background-image:url(/images/profile_images/${this.data.company_user_id}.png);"></div>
        </div>
      </div>
      <div class="details">
        <div class="details-1">
          <b>${this.getUserFullName()}</b><br>
          ${this.data.unit ? `${this.data.unit}<br>` : ''}
          <br>
          ${this.data.email ? `
            <i class="fas fa-envelope-square"></i>
            <a href="mailto:${this.data.email}">${this.data.email}</a><br>
          ` : ''}
          ${this.data.phone ? `
            <i class="fas fa-phone-square"></i>
            <a href="tel:${this.data.phone.replace(/\s/g, '')}">${this.data.phone}</a><br>
          ` : ''}
          ${this.data.skype ? `
            <i class="fab fa-skype"></i>
            <a href="skype:${this.data.skype}?call">${this.data.skype}</a><br>
          ` : ''}
        </div>
        <div class="details-2">
          ${this.data.address_name ? `
            <a class="mundus-agri" href="https://www.mundus-agri.eu/mundus-agri/addressDetails/${this.data.global_address_id}" title="${this.translations.viewOnMundusAgri}">
              ${this.data.address_name}
            </a><br>` : ''
          }
          ${this.data.street ? `${this.data.street}<br>` : ''}
          ${this.data.zip && this.data.city ? `${this.data.zip} ${this.data.city}<br>` : ''}

          ${this.data.country_name ? `
            <div class="country">
              <div>
                <img src="/projects/application/dist/images/icons/flags/4x3/${this.data.country_code.toLowerCase()}.svg" alt="${this.data.country_name}" title="${this.data.country_name}"> <span>${this.data.country_name}</span>
              </div>
            </div>
          ` : ''}

        </div>
      </div>
      ${this.data.global_address_id || this.addressId ? `
        <div class="buttons">
          <a class="button small show-notes">${this.translations.contactDetailsModal.notes}</a>
        </div>
      ` : ''}
    `
  }
}